/**
 * `vendorPayments.*` — purchase-side money-out ledger + per-bill allocation.
 *
 * system-decisions §13: no auto-disbursement, ever. The Founder paid via
 * netbanking; we record the payment, compute TDS at the recorded date, and
 * flip the bill status. Mirror of `sales/receipts/` on the purchase side.
 *
 * §2 + §14 + persona-journeys F11: amounts ≥ per-Company threshold trigger
 * Founder OTP. The first attempt (no `approvalId`) inserts a
 * `pending_approvals` row and returns `{ status: 'awaiting_approval' }`. The
 * second attempt (passing the approved `approvalId`) bypasses the threshold
 * check and writes the payment. The approval row's `requestPayload` carries
 * the validated input; the second attempt is verified against it.
 *
 * ADR-0012 §6 forward-compat: `vendor_payments` rows project to
 * Dr Trade Payables · Cr Bank/Cash · Cr TDS Payable. Every field needed for
 * that posting lives on the row already (`vendor_id`, `tds_section`,
 * `tds_rate_bp`, `gross_paise`, `tds_paise`, `net_paise`). `bank_account_id`
 * arrives nullable when the cash module migrates in; no schema change here.
 */
import { z } from "zod";
import { uuid, paged, isoDate, moneyPaise } from "../_common.js";

/** Payment mode the Founder used in netbanking. Mirrors the schema enum. */
export const vendorPaymentMode = z.enum([
  "neft",
  "rtgs",
  "imps",
  "upi",
  "cheque",
  "cash",
  "card",
  "other",
]);

/**
 * Status the operator sees on a payment row. The DB enum has five values
 * (`queued / awaiting_approval / approved / paid / void`); V1.0 writes only
 * three: `paid` (terminal happy path), `void` (reversal terminal), and the
 * `awaiting_approval` path is represented by a `pending_approvals` row, not
 * a `vendor_payments` row (RLS grants INSERT-only — no UPDATE path).
 */
export const vendorPaymentStatus = z.enum([
  "queued",
  "awaiting_approval",
  "approved",
  "paid",
  "void",
]);

// ── record ────────────────────────────────────────────────────────
/**
 * Allocation invariant: Σ allocations.paise === grossPaise.
 * `tdsPaise` is computed by the domain (system knows the rate); the operator
 * never types a TDS amount. `netPaise = grossPaise − tdsPaise` is what
 * actually left the Founder's bank.
 *
 * `approvalId` lets the second-attempt path bypass the threshold gate after
 * a Founder has approved the payload in the inbox. The domain verifies
 * (a) the approval exists, (b) `status='approved'`, (c) `kind='vendor_payment_above_threshold'`,
 * (d) the stored `requestPayload` deep-equals this input. Without (d), an
 * approvalId leaked from one payment couldn't be reused for another.
 */
export const recordInput = z.object({
  vendorId: uuid,
  paidOn: isoDate,
  grossPaise: moneyPaise,
  mode: vendorPaymentMode,
  /** Bank ref / cheque / UPI txn id — surfaces on the activity row. */
  reference: z.string().trim().max(64).optional(),
  allocations: z
    .array(
      z.object({
        billId: uuid,
        paise: moneyPaise,
      }),
    )
    .min(1)
    .max(20),
  /** Set on the second attempt after a Founder approves the queued payment. */
  approvalId: uuid.optional(),
  /**
   * Account the money left from (bank / cash / credit_card). When set, the
   * payment's `net` debits this account's running balance — e.g. a `card`
   * payment lands the spend on the credit-card account as liability. Optional
   * for back-compat with rows that don't pin a funding account (matched to a
   * statement line later, V1.x).
   */
  bankAccountId: uuid.optional(),
});

export const recordOutput = z.object({
  /**
   * When `status='paid'`: the `vendor_payments.id` of the inserted row.
   * When `status='awaiting_approval'`: the `pending_approvals.id` — the
   * caller stores this and passes it back as `approvalId` once the Founder
   * approves.
   */
  id: uuid,
  status: z.enum(["paid", "awaiting_approval"]),
  tdsPaise: moneyPaise,
  netPaise: moneyPaise,
  /** Bill status flips triggered by this payment — lets the SPA invalidate the
   *  exact rows that changed. Empty array when `status='awaiting_approval'`. */
  statusTransitions: z.array(
    z.object({
      billId: uuid,
      fromStatus: z.string(),
      toStatus: z.string(),
    }),
  ),
});

// ── find approved approval for bill ──────────────────────────────
/**
 * Lookup an approved `vendor_payment_above_threshold` approval whose payload
 * allocates against this bill. Powers the dialog's prefill so a resubmit
 * carries the `approvalId` — otherwise each resubmit silently files a
 * duplicate pending approval and the bill never flips to paid via the UI.
 *
 * Returns the form-relevant slice of the stored intent. Editing any field
 * after prefill (different gross, different paid-on, different mode/ref)
 * trips the server-side `samePaymentIntent` check and fails safely — the
 * approval can only be used to record the exact payment that was approved.
 */
export const findApprovedApprovalForBillInput = z.object({
  vendorId: uuid,
  billId: uuid,
});

export const findApprovedApprovalForBillOutput = z
  .object({
    approvalId: uuid,
    paidOn: isoDate,
    grossPaise: moneyPaise,
    mode: vendorPaymentMode,
    reference: z.string().nullable(),
  })
  .nullable();

// ── get ───────────────────────────────────────────────────────────
export const getInput = z.object({ id: uuid });

export const vendorPaymentRow = z.object({
  id: uuid,
  vendorId: uuid,
  paidOn: isoDate,
  grossPaise: moneyPaise,
  tdsPaise: moneyPaise,
  netPaise: moneyPaise,
  tdsSection: z.string().nullable(),
  tdsRateBp: z.string().nullable(),
  mode: vendorPaymentMode,
  reference: z.string().nullable(),
  status: vendorPaymentStatus,
  allocations: z.array(
    z.object({
      billId: uuid,
      paise: moneyPaise,
    }),
  ),
  voidsId: uuid.nullable(),
  createdAt: z.string().datetime(),
});

export const getOutput = z.object({
  payment: vendorPaymentRow,
  vendor: z.object({
    id: uuid,
    name: z.string(),
    tdsSection: z.string().nullable(),
  }),
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  vendorId: uuid.optional(),
  billId: uuid.optional(),
  /** Narrow to one payment mode (drives the Mode filter chip). */
  mode: vendorPaymentMode.optional(),
  /** Free-text search across vendor name + payment reference (ILIKE-substring). */
  search: z.string().trim().max(120).optional(),
  /** Inclusive lower bound on `paid_on` (YYYY-MM-DD). */
  dateFrom: isoDate.optional(),
  /** Inclusive upper bound on `paid_on` (YYYY-MM-DD). */
  dateTo: isoDate.optional(),
});

const listRow = z.object({
  id: uuid,
  vendorId: uuid,
  vendorName: z.string(),
  /** Vendor city — sub-line on the ledger row ("Pune"). Null = unset. */
  vendorCity: z.string().nullable(),
  /** Snapshot of `vendors.tds_section` at payment time. Null = no TDS. */
  vendorTdsSection: z.string().nullable(),
  paidOn: isoDate,
  grossPaise: moneyPaise,
  tdsPaise: moneyPaise,
  netPaise: moneyPaise,
  mode: vendorPaymentMode,
  reference: z.string().nullable(),
  status: vendorPaymentStatus,
  /**
   * Bills allocated against this payment. Single-bill is the V1.0 SPA
   * default; the backend supports multi-bill. The ledger renders the
   * first as "Bill #" link with "+ N more" affordance when applicable.
   */
  bills: z.array(
    z.object({
      id: uuid,
      docNumber: z.string().nullable(),
    }),
  ),
  createdAt: z.string().datetime(),
});

export const listOutput = paged(listRow);

// ── void ──────────────────────────────────────────────────────────
/**
 * Reverse a recorded payment. Append-only: this inserts a NEW vendor_payments
 * row with `status='void'`, `voids_id` pointing at the original, and negative
 * amounts. The original row is not mutated (RLS grants INSERT-only).
 *
 * Founder + step-up (stepUpFounderProcedure in apps/api/src/trpc.ts).
 */
export const voidInput = z.object({
  id: uuid,
  reason: z.string().trim().min(4).max(500),
});

export const voidOutput = z.object({
  /** The reversing row's id. The original `id` stays at `status='paid'`. */
  reversingId: uuid,
  statusTransitions: z.array(
    z.object({
      billId: uuid,
      fromStatus: z.string(),
      toStatus: z.string(),
    }),
  ),
});

// ── derived TS types ──────────────────────────────────────────────
export type RecordInput = z.infer<typeof recordInput>;
export type RecordOutput = z.infer<typeof recordOutput>;
export type FindApprovedApprovalForBillInput = z.infer<typeof findApprovedApprovalForBillInput>;
export type FindApprovedApprovalForBillOutput = z.infer<typeof findApprovedApprovalForBillOutput>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type VoidInput = z.infer<typeof voidInput>;
export type VoidOutput = z.infer<typeof voidOutput>;
export type VendorPaymentRow = z.infer<typeof vendorPaymentRow>;
export type ListRow = z.infer<typeof listRow>;
export type VendorPaymentMode = z.infer<typeof vendorPaymentMode>;
export type VendorPaymentStatus = z.infer<typeof vendorPaymentStatus>;
